import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Square, MessageSquare, Bot, Loader2, Zap, Clock, Hash, ListChecks } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSimulationStore } from '@/store/simulationStore';
import { useAgentStore } from '@/store/agentStore';
import { Simulation } from '@/services/simulation.service';
import { SimulationTaskPanel } from '@/components/simulations/SimulationTaskPanel';

const statusConfig: Record<
  Simulation['status'],
  { label: string; bg: string; text: string; dot: string; pulse?: boolean }
> = {
  pending: { label: 'Pending', bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  running: { label: 'Running', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', pulse: true },
  completed: { label: 'Completed', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  stopped: { label: 'Stopped', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  failed: { label: 'Failed', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

function StatusBadge({ status }: { status: Simulation['status'] }) {
  const cfg = statusConfig[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? 'animate-pulse' : ''}`} />
      <span className="text-xs font-semibold">{cfg.label}</span>
    </span>
  );
}

export function SimulationsPage() {
  const { id: simIdFromUrl } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    bots,
    fetchBots,
    simulations,
    simulationsLoading,
    fetchSimulations,
    startSimulation,
    stopSimulation,
    advanceSimulation,
    activeSimulation,
    activeMessages,
    messagesLoading,
    setActiveSimulation,
    fetchSimulation,
    fetchMessages,
  } = useSimulationStore();

  const { agents, fetchAgents } = useAgentStore();

  const [selectedBotId, setSelectedBotId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [delayMs, setDelayMs] = useState(3000);
  const [maxTurns, setMaxTurns] = useState(20);
  const [runUntilTasksComplete, setRunUntilTasksComplete] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const activeBots = bots.filter((b) => b.status === 'active');

  // Load initial data
  useEffect(() => {
    fetchBots().catch(() => toast.error('Failed to load bots'));
    fetchAgents().catch(() => toast.error('Failed to load agents'));
    fetchSimulations().catch(() => toast.error('Failed to load simulations'));
  }, [fetchBots, fetchAgents, fetchSimulations]);

  // Sync URL param to active simulation
  useEffect(() => {
    if (!simIdFromUrl) {
      // URL is /simulations with no id -- clear active simulation
      if (activeSimulation) {
        setActiveSimulation(null);
      }
      return;
    }

    // If the active simulation already matches the URL, nothing to do
    if (activeSimulation?.id === simIdFromUrl) return;

    // Try to find the simulation in the already-loaded list
    const found = simulations.find((s) => s.id === simIdFromUrl);
    if (found) {
      setActiveSimulation(found);
      fetchMessages(found.id).catch(() => {});
    } else if (!simulationsLoading) {
      // Not in the list (could be a direct URL visit) -- fetch it directly
      fetchSimulation(simIdFromUrl)
        .then(() => fetchMessages(simIdFromUrl).catch(() => {}))
        .catch(() => {
          toast.error('Simulation not found');
          navigate('/simulations', { replace: true });
        });
    }
  }, [simIdFromUrl, simulations, simulationsLoading, activeSimulation?.id, setActiveSimulation, fetchSimulation, fetchMessages, navigate]);

  // Polling for active simulation — calls advanceSimulation to drive the next turn
  const advancingRef = useRef(false);
  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (activeSimulation && activeSimulation.status === 'running') {
      const delay = activeSimulation.config?.delay_ms || 3000;
      pollingRef.current = setInterval(async () => {
        if (advancingRef.current) return; // skip if previous advance still in-flight
        advancingRef.current = true;
        try {
          await advanceSimulation(activeSimulation.id);
          await fetchMessages(activeSimulation.id);
          setRefreshCounter(c => c + 1);
        } catch {
          // ignore — advance will mark simulation as failed if there's an error
        } finally {
          advancingRef.current = false;
        }
      }, Math.max(delay, 2000));
    } else if (activeSimulation && activeSimulation.status === 'pending') {
      // Pending: just poll for status updates
      pollingRef.current = setInterval(() => {
        fetchSimulation(activeSimulation.id).catch(() => {});
      }, 2000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [activeSimulation?.id, activeSimulation?.status, advanceSimulation, fetchSimulation, fetchMessages]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages]);

  const handleStart = async () => {
    if (!selectedBotId || !selectedAgentId) {
      toast.error('Select both a bot and an agent');
      return;
    }

    setIsStarting(true);
    try {
      const sim = await startSimulation({
        bot_id: selectedBotId,
        agent_id: selectedAgentId,
        config: { delay_ms: delayMs, max_turns: maxTurns, run_until_tasks_complete: runUntilTasksComplete },
      });
      toast.success('Simulation started');
      setActiveSimulation(sim);
      fetchMessages(sim.id).catch(() => {});
      navigate(`/simulations/${sim.id}`);
    } catch {
      toast.error('Failed to start simulation');
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async (id: string) => {
    setStoppingId(id);
    try {
      await stopSimulation(id);
      toast.success('Simulation stopped');
    } catch {
      toast.error('Failed to stop simulation');
    } finally {
      setStoppingId(null);
    }
  };

  const handleSelectSimulation = (sim: Simulation) => {
    setActiveSimulation(sim);
    fetchMessages(sim.id).catch(() => {});
    navigate(`/simulations/${sim.id}`);
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Simulations</h1>
        <p className="text-gray-500 mt-1">Run and monitor bot vs agent conversation simulations</p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left panel: Start form + History */}
        <div className="space-y-6">
          {/* Start Simulation Card */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Start Simulation</h2>
            </div>

            {activeBots.length === 0 ? (
              <div className="text-center py-6">
                <Bot className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-3">No active bots available</p>
                <a
                  href="/bots"
                  className="text-sm font-medium text-rose-600 hover:text-rose-700 transition-colors"
                >
                  Create a bot first
                </a>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Bot selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Bot</label>
                  <select
                    value={selectedBotId}
                    onChange={(e) => setSelectedBotId(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm bg-white"
                  >
                    <option value="">Select a bot...</option>
                    {activeBots.map((bot) => (
                      <option key={bot.id} value={bot.id}>
                        {bot.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Agent selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Agent</label>
                  <select
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm bg-white"
                  >
                    <option value="">Select an agent...</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Config inputs */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <Clock className="w-3.5 h-3.5 inline mr-1" />
                      Delay (ms)
                    </label>
                    <input
                      type="number"
                      value={delayMs}
                      onChange={(e) => setDelayMs(parseInt(e.target.value) || 0)}
                      min={500}
                      step={500}
                      className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <Hash className="w-3.5 h-3.5 inline mr-1" />
                      {runUntilTasksComplete ? 'Safety Limit' : 'Max Turns'}
                    </label>
                    <input
                      type="number"
                      value={maxTurns}
                      onChange={(e) => setMaxTurns(parseInt(e.target.value) || 1)}
                      min={1}
                      max={100}
                      className={`w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm ${runUntilTasksComplete ? 'opacity-60' : ''}`}
                    />
                  </div>
                </div>

                {/* Run until tasks complete toggle */}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={runUntilTasksComplete}
                      onChange={(e) => {
                        setRunUntilTasksComplete(e.target.checked);
                        if (e.target.checked) setMaxTurns(50);
                        else setMaxTurns(20);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-rose-600 peer-focus:ring-2 peer-focus:ring-rose-300 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ListChecks className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                      Run until all tasks complete
                    </span>
                  </div>
                </label>

                {/* Start button */}
                <button
                  onClick={handleStart}
                  disabled={isStarting || !selectedBotId || !selectedAgentId}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Start Simulation
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Simulation History */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
            <div className="px-6 py-4 border-b border-[#1a1a2e]">
              <h2 className="text-lg font-bold text-gray-900">History</h2>
            </div>

            {simulationsLoading ? (
              <div className="p-12 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-rose-600 animate-spin" />
              </div>
            ) : simulations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No simulations yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                {simulations.map((sim) => (
                  <button
                    key={sim.id}
                    onClick={() => handleSelectSimulation(sim)}
                    className={`w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors ${
                      activeSimulation?.id === sim.id ? 'bg-rose-50 border-l-2 border-rose-600' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {sim.bot_name || 'Bot'} vs {sim.agent_name || 'Agent'}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-500">
                            {sim.total_turns} turns
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDate(sim.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={sim.status} />
                        {(sim.status === 'running' || sim.status === 'pending') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStop(sim.id);
                            }}
                            disabled={stoppingId === sim.id}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Stop simulation"
                          >
                            {stoppingId === sim.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right panel: Live Simulation View */}
        <div className="md:col-span-2">
          {activeSimulation ? (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] flex flex-col h-[calc(100vh-220px)]">
              {/* Simulation Header */}
              <div className="px-6 py-4 border-b border-[#1a1a2e] flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {activeSimulation.bot_name || 'Bot'} vs {activeSimulation.agent_name || 'Agent'}
                    </h3>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-500">
                        {activeSimulation.total_turns} / {activeSimulation.config.max_turns} turns
                      </span>
                      {activeSimulation.config.run_until_tasks_complete && (
                        <span className="inline-flex items-center gap-1 text-xs text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">
                          <ListChecks className="w-3 h-3" />
                          Until tasks done
                        </span>
                      )}
                      {activeSimulation.started_at && (
                        <span className="text-xs text-gray-400">
                          Started {formatTime(activeSimulation.started_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={activeSimulation.status} />
                  {(activeSimulation.status === 'running' || activeSimulation.status === 'pending') && (
                    <button
                      onClick={() => handleStop(activeSimulation.id)}
                      disabled={stoppingId === activeSimulation.id}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                    >
                      {stoppingId === activeSimulation.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Stopping...
                        </>
                      ) : (
                        <>
                          <Square className="w-4 h-4" />
                          Stop
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Error message */}
              {activeSimulation.error_message && (
                <div className="mx-6 mt-4 p-3 bg-red-50 border-2 border-[#1a1a2e] rounded-xl">
                  <p className="text-sm text-red-700">{activeSimulation.error_message}</p>
                </div>
              )}

              {/* Message Feed + Task Panel */}
              <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messagesLoading && activeMessages.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 text-rose-600 animate-spin" />
                    </div>
                  ) : activeMessages.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          {activeSimulation.status === 'pending'
                            ? 'Waiting for simulation to start...'
                            : 'No messages yet'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    activeMessages.map((msg) => {
                      const isAgent = msg.sender_type === 'agent';
                      const isSystem = msg.sender_type === 'system';

                      if (isSystem) {
                        return (
                          <div key={msg.id} className="flex justify-center">
                            <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
                              {msg.content}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-xl px-4 py-3 ${
                              isAgent
                                ? 'bg-rose-500 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-xs font-semibold ${
                                  isAgent ? 'text-rose-100' : 'text-gray-500'
                                }`}
                              >
                                {isAgent ? 'Agent' : 'Customer (Bot)'}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                            <p
                              className={`text-xs mt-1.5 ${
                                isAgent ? 'text-rose-200' : 'text-gray-400'
                              }`}
                            >
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {activeSimulation.conversation_id && (
                  <div className="w-[340px] border-l border-[#1a1a2e] overflow-hidden bg-white flex-shrink-0">
                    <SimulationTaskPanel
                      conversationId={activeSimulation.conversation_id}
                      refreshTrigger={refreshCounter}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Empty state */
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] flex items-center justify-center h-[calc(100vh-220px)]">
              <div className="text-center">
                <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">No simulation selected</h3>
                <p className="text-sm text-gray-500">
                  Select a simulation to view messages or start a new one
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SimulationsPage;
